import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Mission.css';
import gradient1 from '../assets/gradient_1.png';
import gradient2 from '../assets/gradient_2.png';

gsap.registerPlugin(ScrollTrigger);

const Mission = () => {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isScrollingRef = useRef(false);
  const [isActive, setIsActive] = useState(false);

  const contents = [
    {
      id: 1,
      title: "Make Creator Happy",
      subtitle: "",
      description: "",
      background: gradient1,
    },
    {
      id: 2,
      title: "",
      subtitle: "",
      description: "まず、クリエイターを幸せに。\n創る人が満たされる社会を目指します。",
      background: gradient2,
    },
    {
      id: 3,
      title: "",
      subtitle: "Give Art a Structure",
      description:
        "アートを、構造で支える。\n感性に頼るだけではなく、アートを仕事として再現可能に。\n戦略的に構築し、クリエイティブの本質を届けます。",
      background: gradient1,
    },
    {
      id: 4,
      title: "",
      subtitle: "Be Them",
      description:
        "相手の感情を、自分の軸に。\n真の共感力で、\"本当に喜ばれる価値\"をともに追求します。",
      background: gradient2,
    },
  ];

  const goToNext = useCallback(() => {
    if (currentIndex < contents.length - 1 && !isScrollingRef.current) {
      isScrollingRef.current = true;
      setCurrentIndex((prev) => prev + 1);
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, [currentIndex, contents.length]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0 && !isScrollingRef.current) {
      isScrollingRef.current = true;
      setCurrentIndex((prev) => prev - 1);
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, [currentIndex]);

  // IntersectionObserver: セクションがより中央に近い位置に来てからアクティブ化
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const rect = entry.boundingClientRect;
        const windowHeight = window.innerHeight;
        
        // セクションの上端が画面の上半分より上にあり、下端が画面の下半分より下にある時
        const isWellCentered = rect.top <= windowHeight * 0.2 && rect.bottom >= windowHeight * 0.8;
        
        setIsActive(entry.isIntersecting && isWellCentered);
      },
      {
        root: null,
        threshold: 0.8, // 80%以上表示されている時
        rootMargin: '0px', // マージンなし
      }
    );

    const target = containerRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, []);

  // body スクロールロック：最後のスライド以外で有効化
  useEffect(() => {
    const shouldLock = isActive && currentIndex < contents.length - 1;

    if (shouldLock) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isActive, currentIndex, contents.length]);

  // スクロール制御イベント（最後のスライドでは解除）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (!isActive || isScrollingRef.current || currentIndex >= contents.length - 1) return;
      e.preventDefault();
      e.deltaY > 0 ? goToNext() : goToPrevious();
    };

    const handleKeyDown = (e) => {
      if (!isActive || isScrollingRef.current || currentIndex >= contents.length - 1) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevious();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, currentIndex, goToNext, goToPrevious, contents.length]);

  // GSAPアニメーション
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const content = container.querySelector('.content-item');
    const background = container.querySelector('.background-image');

    gsap.fromTo(
      content,
      {
        opacity: 0,
        scale: 0.95,
        y: 40,
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
      }
    );

    gsap.fromTo(
      background,
      {
        opacity: 0,
        scale: 1.1,
      },
      {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'power2.out',
      }
    );
  }, [currentIndex]);

  const currentContent = contents[currentIndex];

  return (
    <div className="mission-wrapper" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div
        ref={containerRef}
        className="mission-container"
        style={{
          width: '100%',
          height: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 背景画像 */}
        <div
          key={`bg-${currentContent.id}`}
          className="background-image"
          style={{
            backgroundImage: `url(${currentContent.background})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
          }}
        />

        {/* コンテンツ */}
        <div className="content-layer" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div
            key={`content-${currentContent.id}`}
            className="content-item"
            style={{
              position: 'absolute',
              top: '32%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              maxWidth: '1200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: '2rem',
              opacity: 0,
            }}
          >
            {currentContent.title && <h1 className="title">{currentContent.title}</h1>}
            {currentContent.subtitle && <h2 className="subtitle">{currentContent.subtitle}</h2>}
            {currentContent.description && (
              <p className="description">
                {currentContent.description.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

export default Mission;